/**
 * The native status bar, driven by the LUMINANCE of the surface under it.
 *
 * iOS draws the clock, wifi and battery in one of two inks, and which one is
 * legible depends on what is painted behind them. That is not the same question
 * as "what theme is the user in", and it is not the opposite of it either:
 *
 * - Some surfaces are FROZEN. `components/brand/entry-styles.ts` pins the entry
 *   canvas to cream in both themes, so a dark-mode reader still needs dark ink.
 * - Some surfaces are FIXED the other way - the home header's brand gradient is
 *   dark in every theme.
 * - Most surfaces FOLLOW the theme. `--color-landing-bg` is `#f5f3ee` in light
 *   and near-black in dark (`index.css:477` / `:2029`), so for those the theme
 *   genuinely is the right input.
 *
 * Naming the argument by luminance rather than by colour is what keeps the
 * plugin's confusing enum from leaking: the mapping below is an identity.
 */

import { isNativePlatform } from './platform';

/**
 * The luminance of whatever is painted behind the status bar.
 *
 * `dark` surfaces need light glyphs; `light` surfaces need dark glyphs.
 */
export type StatusBarSurface = 'light' | 'dark';

/**
 * The surface we want next, and whether someone is already on their way to
 * apply it.
 *
 * This function awaits a dynamic import before it touches the plugin, so two
 * calls in quick succession - opening and closing the workspaces reveal inside
 * its 420ms transition, say - overlap. Rather than have each call race, the
 * later one just leaves its surface here and the in-flight one picks it up:
 * one bridge call, and the newest intent always wins.
 *
 * The first version of this used a monotonic ticket and had each stale call
 * stand down. That deadlocks: with two overlapping calls BOTH tickets are stale
 * by the time either resolves, so nothing is applied at all - a worse failure
 * than the race it was guarding. Coalescing has no such hole because the value,
 * not the caller, is what survives.
 */
let desiredSurface: StatusBarSurface | null = null;
let applyInFlight = false;

/**
 * Who is currently claiming the status bar, innermost last.
 *
 * A single "current surface" cannot express an overlay, because closing one has
 * to RESTORE what was underneath and the overlay has no idea what that was. A
 * compose sheet opened over home must hand the bar back to the brand header;
 * opened over tasks it must hand it back to whatever the theme says. So claims
 * nest, and the top of the stack wins.
 */
const claims: { id: string; surface: StatusBarSurface }[] = [];

/**
 * Claim the status bar for as long as a surface is on screen.
 *
 * `id` identifies the claimant so a re-render updates its claim in place rather
 * than stacking a second one.
 */
export function claimStatusBarSurface(id: string, surface: StatusBarSurface): void {
  const existing = claims.findIndex((claim) => claim.id === id);
  if (existing >= 0) {
    claims[existing] = { id, surface };
  } else {
    claims.push({ id, surface });
  }
  void applyStatusBarSurface(claims[claims.length - 1].surface);
}

/**
 * Give the status bar back, and repaint for whatever was underneath.
 *
 * Releasing a claim that is not on top only removes it - the visible surface is
 * still the top one, and repainting would hand the bar to a screen that is no
 * longer the innermost.
 */
export function releaseStatusBarSurface(id: string): void {
  const index = claims.findIndex((claim) => claim.id === id);
  if (index < 0) {
    return;
  }
  claims.splice(index, 1);

  const top = claims[claims.length - 1];
  if (top) {
    void applyStatusBarSurface(top.surface);
  }
}

/**
 * Paint the status bar for the surface behind it.
 *
 * Prefer `claimStatusBarSurface` from components - it is what makes an overlay
 * restore correctly on close. This stays exported for the one caller that
 * genuinely owns the whole screen and never unmounts.
 *
 * Best-effort by contract: a no-op off native, and a warning rather than a
 * throw if the plugin is missing, because every caller is an effect and none of
 * them can do anything useful with the failure.
 */
export async function applyStatusBarSurface(surface: StatusBarSurface): Promise<void> {
  if (!isNativePlatform()) {
    return;
  }

  desiredSurface = surface;
  if (applyInFlight) {
    // Whoever is mid-import will read the value we just left.
    return;
  }
  applyInFlight = true;

  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar');

    // Drain rather than apply once: a surface set while `setStyle` was awaiting
    // is newer than the one we just painted and has to land too.
    while (desiredSurface !== null) {
      const next = desiredSurface;
      desiredSurface = null;

      // Reads as an identity because the argument is the SURFACE's luminance
      // and the enum is named for the background it suits: `Style.Dark` is
      // "light text for dark backgrounds". Never `Style.Default` - that follows
      // the DEVICE theme, which is the exact bug this module exists to stop.
      await StatusBar.setStyle({ style: next === 'dark' ? Style.Dark : Style.Light });
    }
  } catch (error) {
    console.warn('Failed to apply status bar surface:', error);
  } finally {
    // Must clear even on the error path, or one plugin miss wedges the status
    // bar for the rest of the session.
    applyInFlight = false;
  }
}
